import { router } from "@inertiajs/react"
import { Edit2Icon, MapPinIcon, PlusIcon, StarIcon, Trash2Icon } from "lucide-react"
import { type ReactNode, useEffect, useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AdminLayout } from "@/layouts/AdminLayout"
import { PoiIcon } from "@/lib/poi-icon"
import type { CategoryOption, PoiFilters, PoiMediaItem, PoiRow } from "@/lib/types"
import { PoiForm } from "./Form"

interface PropertySummary {
  id: number
  name: string
  region_id: number | null
}

interface Props {
  property: PropertySummary
  private_pois: PoiRow[]
  public_pois: PoiRow[]
  categories: CategoryOption[]
  filters: PoiFilters
  editing_poi?: PoiRow
  media?: PoiMediaItem[]
  urls: { index: string; create: string }
  update_url?: string
  media_create_url?: string
}

interface PoiCardProps {
  poi: PoiRow
  onEdit: (poi: PoiRow) => void
  onDelete: (poi: PoiRow) => void
  readOnly?: boolean
}

function PoiCard({ poi, onEdit, onDelete, readOnly = false }: PoiCardProps) {
  return (
    <Card className="gap-3 py-4">
      <CardHeader className="pb-0">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base leading-snug">{poi.name}</CardTitle>
          {poi.is_featured && (
            <StarIcon className="size-4 shrink-0 fill-yellow-400 text-yellow-400" />
          )}
        </div>
        {poi.address && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPinIcon className="size-3" />
            {poi.address}
          </p>
        )}
      </CardHeader>

      <CardContent className="pb-0">
        {poi.categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {poi.categories.map((cat) => (
              <Badge key={cat.id} variant="secondary" className="text-xs">
                {cat.icon && <PoiIcon name={cat.icon} size={12} className="mr-1" />}
                {cat.name}
              </Badge>
            ))}
          </div>
        )}
        {poi.tags.length > 0 && (
          <p className="mt-1.5 text-xs text-muted-foreground">
            {poi.tags.join(", ")}
          </p>
        )}
      </CardContent>

      <CardFooter className="justify-between pt-0">
        <Badge
          variant={poi.status === "approved" ? "default" : "outline"}
          className="text-xs"
        >
          {poi.status}
        </Badge>

        {!readOnly ? (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => onEdit(poi)}
              aria-label="Edit POI"
            >
              <Edit2Icon className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-destructive hover:text-destructive"
              onClick={() => onDelete(poi)}
              aria-label="Delete POI"
            >
              <Trash2Icon className="size-3.5" />
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled>
            Add to Guide
          </Button>
        )}
      </CardFooter>
    </Card>
  )
}

interface FilterBarProps {
  filters: PoiFilters
  categories: CategoryOption[]
  indexUrl: string
}

function FilterBar({ filters, categories, indexUrl }: FilterBarProps) {
  function update(patch: Partial<PoiFilters>) {
    const next: Record<string, string | undefined> = {
      search:      filters.search,
      category_id: filters.category_id,
      featured:    filters.featured,
      ...patch,
    }
    // Remove empty values
    Object.keys(next).forEach((k) => {
      if (!next[k]) delete next[k]
    })
    router.get(indexUrl, next, { preserveState: true, replace: true })
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Input
        placeholder="Search POIs…"
        className="h-8 w-52 text-sm"
        defaultValue={filters.search ?? ""}
        onChange={(e) => {
          const v = e.target.value
          const timer = setTimeout(() => update({ search: v || undefined }), 400)
          return () => clearTimeout(timer)
        }}
      />

      <Select
        value={filters.category_id ?? "all"}
        onValueChange={(v) => update({ category_id: v === "all" ? undefined : v })}
      >
        <SelectTrigger className="h-8 w-44 text-sm">
          <SelectValue placeholder="All categories" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All categories</SelectItem>
          {categories.map((cat) => (
            <SelectItem key={cat.id} value={String(cat.id)}>
              {cat.icon && <PoiIcon name={cat.icon} size={12} className="mr-1" />}
              {cat.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant={filters.featured === "true" ? "secondary" : "outline"}
        size="sm"
        className="h-8"
        onClick={() =>
          update({ featured: filters.featured === "true" ? undefined : "true" })
        }
      >
        <StarIcon className="mr-1.5 size-3.5" />
        Featured only
      </Button>
    </div>
  )
}

export default function PropertyPoisIndex({
  property,
  private_pois,
  public_pois,
  categories,
  filters,
  editing_poi,
  media,
  urls,
  update_url,
  media_create_url,
}: Props) {
  const [formOpen, setFormOpen] = useState(!!editing_poi)
  const [editingPoi, setEditingPoi] = useState<PoiRow | null>(editing_poi ?? null)
  const [deleteTarget, setDeleteTarget] = useState<PoiRow | null>(null)

  // Sync when Inertia redirects back to the edit route (component doesn't remount)
  useEffect(() => {
    setEditingPoi(editing_poi ?? null)
    setFormOpen(!!editing_poi)
  }, [editing_poi])

  function openCreate() {
    setEditingPoi(null)
    setFormOpen(true)
  }

  function openEdit(poi: PoiRow) {
    router.visit(poi.edit_url)
  }

  function closeForm() {
    router.visit(urls.index, { replace: true })
  }

  function handleDelete(poi: PoiRow) {
    setDeleteTarget(poi)
  }

  function confirmDelete() {
    if (!deleteTarget) return
    router.delete(deleteTarget.delete_url)
    setDeleteTarget(null)
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">POIs</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
        </div>
        <Button size="sm" onClick={openCreate}>
          <PlusIcon className="mr-1.5 size-4" />
          Add POI
        </Button>
      </div>

      <Tabs defaultValue="private">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="private">
              Private POIs ({private_pois.length})
            </TabsTrigger>
            <TabsTrigger value="public">
              Public POIs — Regional ({public_pois.length})
            </TabsTrigger>
          </TabsList>

          <FilterBar
            filters={filters}
            categories={categories}
            indexUrl={urls.index}
          />
        </div>

        <TabsContent value="private">
          {private_pois.length === 0 ? (
            <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
              <MapPinIcon className="mx-auto mb-3 size-8 opacity-30" />
              <p className="text-sm">No private POIs yet.</p>
              <Button variant="link" size="sm" onClick={openCreate} className="mt-1">
                Add your first one
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {private_pois.map((poi) => (
                <PoiCard
                  key={poi.id}
                  poi={poi}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="public">
          {public_pois.length === 0 ? (
            <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
              <MapPinIcon className="mx-auto mb-3 size-8 opacity-30" />
              <p className="text-sm">
                {property.region_id
                  ? "No approved public POIs in your region yet."
                  : "This property has no region assigned."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {public_pois.map((poi) => (
                <PoiCard
                  key={poi.id}
                  poi={poi}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  readOnly
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Sheet open={formOpen} onOpenChange={(open) => !open && closeForm()}>
        <SheetContent className="overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{editingPoi ? "Edit POI" : "Add POI"}</SheetTitle>
          </SheetHeader>
          {formOpen && (
            <PoiForm
              poi={editingPoi ?? undefined}
              categories={categories}
              create_url={urls.create}
              update_url={update_url}
              media_create_url={media_create_url}
              media={editingPoi ? media : undefined}
              onClose={closeForm}
            />
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

PropertyPoisIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
